import { useModals } from '@frontend/modal';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EMAIL_REGION_DEFAULT_COLORS } from './dom/email-regions';
import { useHtmlTemplateEditor } from './editor-api';
import type { EmailRegion, VisualEditorAlign, VisualEditorCellEdge, VisualEditorCommand } from './editor-types';
import { CELL_BORDER_EDGES, HTML_EDITOR_DEFAULT_FONT_FAMILY, stepHtmlEditorFontSize } from './editor-types';
import { ImageModal, type ImageModalData } from './image-modal/image-modal.component';
import { normalizeHref, normalizeImageUrl } from './link-href';
import { LinkModal, type LinkModalData } from './link-modal/link-modal.component';

type PromiseResolve = (_data: LinkModalData | null) => void;
type ImagePromiseResolve = (_data: ImageModalData | null) => void;

export const useFormattingToolbar = () => {
    const { executeCommand, selectionStyle, registerFontSizeSelectionHandler } = useHtmlTemplateEditor();
    const { openModal, closeModal } = useModals();
    const linkModalId = 'html-template-link-modal';
    const imageModalId = 'html-template-image-modal';
    const [heading, setHeading] = useState<string | undefined>('text');
    const [textColor, setTextColor] = useState('#101010');
    const [highlightColor, setHighlightColor] = useState('#ffff00');
    const [fontSize, setFontSize] = useState('12');
    const [fontFamily, setFontFamily] = useState<string>(HTML_EDITOR_DEFAULT_FONT_FAMILY);
    const [align, setAlign] = useState<VisualEditorAlign>('left');
    const [borderEdges, setBorderEdges] = useState<VisualEditorCellEdge[]>([...CELL_BORDER_EDGES]);
    const [borderColor, setBorderColor] = useState('#cccccc');
    const [cellFillColor, setCellFillColor] = useState('#ffffff');
    const [emailRegionColors, setEmailRegionColors] = useState<Record<EmailRegion, string>>({ ...EMAIL_REGION_DEFAULT_COLORS });
    const promiseResolver = useRef<PromiseResolve | null>(null);
    const imagePromiseResolver = useRef<ImagePromiseResolve | null>(null);
    /** Sync source for −/+ — not overwritten by programmatic selectionchange reads. */
    const fontSizeRef = useRef(fontSize);

    useEffect(() => {
        if (selectionStyle.color) setTextColor(selectionStyle.color);
    }, [selectionStyle.color]);

    useEffect(() => {
        if (selectionStyle.backgroundColor) setHighlightColor(selectionStyle.backgroundColor);
    }, [selectionStyle.backgroundColor]);

    useEffect(() => {
        registerFontSizeSelectionHandler((nextSize) => {
            if (!nextSize) return;
            fontSizeRef.current = nextSize;
            setFontSize(nextSize);
        });
        return () => registerFontSizeSelectionHandler(null);
    }, [registerFontSizeSelectionHandler]);

    useEffect(() => {
        if (selectionStyle.fontFamily) setFontFamily(selectionStyle.fontFamily);
    }, [selectionStyle.fontFamily]);

    useEffect(() => {
        if (selectionStyle.align) setAlign(selectionStyle.align);
    }, [selectionStyle.align]);

    useEffect(() => {
        if (selectionStyle.heading) setHeading(selectionStyle.heading);
    }, [selectionStyle.heading]);

    useEffect(() => {
        if (!selectionStyle.inTable) return;
        // Edge toggles are a paint brush — do not reset them from the cell after each
        // command. Default cells keep all four sides, so syncing would always reselect all.
        if (selectionStyle.cellBorderColor) setBorderColor(selectionStyle.cellBorderColor);
        setCellFillColor(selectionStyle.cellFillColor ?? '#ffffff');
    }, [selectionStyle.cellBorderColor, selectionStyle.cellFillColor, selectionStyle.inTable]);

    // Sync edge picker once when the caret enters a table (not after every mutation).
    const wasInTableRef = useRef(false);
    useEffect(() => {
        const inTable = Boolean(selectionStyle.inTable);
        if (inTable && !wasInTableRef.current) {
            setBorderEdges(
                selectionStyle.cellBorderEdges && selectionStyle.cellBorderEdges.length > 0 ? selectionStyle.cellBorderEdges : [...CELL_BORDER_EDGES]
            );
        }
        wasInTableRef.current = inTable;
    }, [selectionStyle.cellBorderEdges, selectionStyle.inTable]);

    useEffect(() => {
        const colors = selectionStyle.emailRegionColors;
        if (!colors) return;
        setEmailRegionColors((current) => ({
            canvas: colors.canvas ?? current.canvas,
            header: colors.header ?? current.header,
            card: colors.card ?? current.card
        }));
    }, [selectionStyle.emailRegionColors]);

    const handleModalConfirm = useCallback(
        (data: LinkModalData) => {
            promiseResolver.current?.(data);
            closeModal(linkModalId);
        },
        [closeModal]
    );

    const handleModalCancel = useCallback(() => {
        promiseResolver.current?.(null);
        closeModal(linkModalId);
    }, [closeModal]);

    const handleUnlink = useCallback(() => {
        promiseResolver.current?.({ href: '', text: '', target: null });
        closeModal(linkModalId);
    }, [closeModal]);

    const openLinkModal = useCallback(
        (initialData: Partial<LinkModalData>, isEditing = false): Promise<LinkModalData | null> => {
            openModal(linkModalId, () => (
                <LinkModal
                    initialData={initialData}
                    isEditing={isEditing}
                    onConfirm={handleModalConfirm}
                    onCancel={handleModalCancel}
                    onUnlink={handleUnlink}
                />
            ));
            return new Promise((resolve) => {
                promiseResolver.current = resolve;
            });
        },
        [handleModalCancel, handleModalConfirm, handleUnlink, openModal]
    );

    const handleImageModalConfirm = useCallback(
        (data: ImageModalData) => {
            imagePromiseResolver.current?.(data);
            closeModal(imageModalId);
        },
        [closeModal]
    );

    const handleImageModalCancel = useCallback(() => {
        imagePromiseResolver.current?.(null);
        closeModal(imageModalId);
    }, [closeModal]);

    const openImageModal = useCallback(
        (initialData: Partial<ImageModalData>): Promise<ImageModalData | null> => {
            openModal(imageModalId, () => (
                <ImageModal
                    initialData={initialData}
                    onConfirm={handleImageModalConfirm}
                    onCancel={handleImageModalCancel}
                />
            ));
            return new Promise((resolve) => {
                imagePromiseResolver.current = resolve;
            });
        },
        [handleImageModalCancel, handleImageModalConfirm, openModal]
    );

    const dispatchCommand = useCallback((command: VisualEditorCommand) => executeCommand(command), [executeCommand]);

    const changeTextColor = useCallback(
        (color: string) => {
            setTextColor(color);
            dispatchCommand({ type: 'foreColor', color });
        },
        [dispatchCommand]
    );

    const changeHighlightColor = useCallback(
        (color: string) => {
            setHighlightColor(color);
            dispatchCommand({ type: 'hiliteColor', color });
        },
        [dispatchCommand]
    );

    const changeFontSize = useCallback(
        (nextSize?: string) => {
            if (!nextSize) return;
            fontSizeRef.current = nextSize;
            setFontSize(nextSize);
            dispatchCommand({ type: 'fontSize', size: `${nextSize}px` });
        },
        [dispatchCommand]
    );

    const stepFontSize = useCallback(
        (direction: 1 | -1) => {
            const next = stepHtmlEditorFontSize(fontSizeRef.current, direction);
            changeFontSize(next);
        },
        [changeFontSize]
    );

    const changeFontFamily = useCallback(
        (nextFamily?: string) => {
            if (!nextFamily) return;
            setFontFamily(nextFamily);
            dispatchCommand({ type: 'fontFamily', family: nextFamily });
        },
        [dispatchCommand]
    );

    const changeAlign = useCallback(
        (nextAlign: VisualEditorAlign) => {
            setAlign(nextAlign);
            dispatchCommand({ type: 'align', align: nextAlign });
        },
        [dispatchCommand]
    );

    const toggleBorderEdge = (edge: VisualEditorCellEdge) => {
        setBorderEdges((current) => {
            const next = new Set(current);
            if (next.has(edge)) next.delete(edge);
            else next.add(edge);
            return CELL_BORDER_EDGES.filter((item) => next.has(item));
        });
    };

    const toggleAllBorderEdges = () => {
        setBorderEdges((current) => (current.length === CELL_BORDER_EDGES.length ? [] : [...CELL_BORDER_EDGES]));
    };

    const changeBorderColor = (color: string) => {
        setBorderColor(color);
        if (borderEdges.length === 0) return;
        dispatchCommand({ type: 'cellBorders', edges: borderEdges, color });
    };

    const clearCellBorders = () => {
        if (borderEdges.length === 0) return;
        dispatchCommand({ type: 'cellBorders', edges: borderEdges, color: '' });
    };

    const changeCellFillColor = (color: string) => {
        setCellFillColor(color);
        dispatchCommand({ type: 'cellFill', color });
    };

    const changeEmailRegionColor = (region: EmailRegion, color: string) => {
        setEmailRegionColors((current) => ({ ...current, [region]: color }));
        dispatchCommand({ type: 'emailRegionFill', region, color });
    };

    const clearCellFill = () => {
        setCellFillColor('#ffffff');
        dispatchCommand({ type: 'cellFill', color: '' });
    };

    const changeHeading = (nextHeading?: string) => {
        const block = (nextHeading && nextHeading !== 'text' ? nextHeading : 'p') as 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        setHeading(nextHeading ?? 'text');
        dispatchCommand({ type: 'formatBlock', block });
    };

    const setLink = useCallback(async () => {
        const activeLink = selectionStyle.activeLink;
        const result = await openLinkModal(
            activeLink
                ? {
                      href: activeLink.href,
                      text: activeLink.text,
                      target: activeLink.target ?? '_self'
                  }
                : {
                      target: '_self'
                  },
            Boolean(activeLink)
        );

        if (result === null) {
            return;
        }

        if (result.href.trim() === '') {
            dispatchCommand({ type: 'unlink' });
            return;
        }

        const href = normalizeHref(result.href);
        if (!href) return;
        dispatchCommand({ type: 'link', href, text: result.text, target: result.target });
    }, [dispatchCommand, openLinkModal, selectionStyle.activeLink]);

    const insertImage = useCallback(async () => {
        const result = await openImageModal({});
        if (!result) return;
        const src = normalizeImageUrl(result.src);
        if (!src) return;
        dispatchCommand({ type: 'image', src, alt: result.alt });
    }, [dispatchCommand, openImageModal]);

    return {
        align,
        bold: Boolean(selectionStyle.bold),
        changeAlign,
        changeBorderColor,
        changeCellFillColor,
        changeEmailRegionColor,
        changeFontFamily,
        changeFontSize,
        changeHeading,
        changeHighlightColor,
        changeTextColor,
        clearCellBorders,
        clearCellFill,
        dispatchCommand,
        emailRegionColors,
        fontFamily,
        fontSize,
        heading,
        highlightColor,
        inTable: Boolean(selectionStyle.inTable),
        insertImage,
        italic: Boolean(selectionStyle.italic),
        borderColor,
        borderEdges,
        cellFillColor,
        setLink,
        stepFontSize,
        textColor,
        toggleAllBorderEdges,
        toggleBorderEdge,
        underline: Boolean(selectionStyle.underline)
    };
};
